// Group Management API Slice - Organization Hierarchy Redesign
// Handles group verification, organization merge, and contact inheritance
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from '../../../Features/API/api';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';

const BASE_URL = getApiBaseUrl();

export const groupManagementApiSlice = createApi({
  reducerPath: 'groupManagementApi',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: ['PendingGroups', 'GroupContacts', 'Organization', 'DeviceGroup'],
  endpoints: (builder) => ({
    // ========== PENDING GROUPS ==========
    fetchPendingGroups: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.source) queryParams.append('source', params.source);
        if (params.organization) queryParams.append('organization', params.organization);
        if (params.status) queryParams.append('status', params.status);
        if (params.page) queryParams.append('page', params.page);
        if (params.pageSize) queryParams.append('page_size', params.pageSize);
        const queryString = queryParams.toString();
        return `fleet/groups/pending/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['PendingGroups'],
    }),

    // ========== GROUP VERIFICATION ==========
    verifyGroup: builder.mutation({
      query: (groupId) => ({
        url: `fleet/groups/${groupId}/verify/`,
        method: 'POST',
      }),
      invalidatesTags: ['PendingGroups', 'DeviceGroup'],
    }),

    rejectGroup: builder.mutation({
      query: ({ groupId, reason }) => ({
        url: `fleet/groups/${groupId}/reject/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['PendingGroups', 'DeviceGroup'],
    }),

    reassignGroup: builder.mutation({
      query: ({ groupId, organizationId, verify = true }) => ({
        url: `fleet/groups/${groupId}/reassign/`,
        method: 'POST',
        body: { organization_id: organizationId, verify },
      }),
      invalidatesTags: ['PendingGroups', 'DeviceGroup', 'Organization'],
    }),

    bulkVerifyGroups: builder.mutation({
      query: (groupIds) => ({
        url: `fleet/groups/bulk-verify/`,
        method: 'POST',
        body: { group_ids: groupIds },
      }),
      invalidatesTags: ['PendingGroups', 'DeviceGroup'],
    }),

    bulkRejectGroups: builder.mutation({
      query: ({ groupIds, reason }) => ({
        url: `fleet/groups/bulk-reject/`,
        method: 'POST',
        body: { group_ids: groupIds, reason },
      }),
      invalidatesTags: ['PendingGroups', 'DeviceGroup'],
    }),

    // ========== ORGANIZATION LIST (Optimized) ==========
    fetchOrganizations: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.pageSize) queryParams.append('page_size', params.pageSize);
        if (params.search) queryParams.append('search', params.search);
        if (params.hasGroups !== undefined) queryParams.append('has_groups', params.hasGroups);
        const queryString = queryParams.toString();
        return `fleet/organizations/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Organization'],
    }),

    // Organization list with full stats (for Organization Management page)
    fetchOrganizationsWithStats: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.pageSize) queryParams.append('page_size', params.pageSize);
        if (params.search) queryParams.append('search', params.search);
        if (params.status) queryParams.append('status', params.status);
        if (params.plan) queryParams.append('plan', params.plan);
        if (params.hasGroups !== undefined) queryParams.append('has_groups', params.hasGroups);
        if (params.inherit_contacts !== undefined) queryParams.append('inherit_contacts', params.inherit_contacts);
        if (params.has_org_contacts !== undefined) queryParams.append('has_org_contacts', params.has_org_contacts);
        if (params.has_group_contacts !== undefined) queryParams.append('has_group_contacts', params.has_group_contacts);
        if (params.include_status_counts) queryParams.append('include_status_counts', 'true');
        queryParams.append('include_stats', 'true');
        const queryString = queryParams.toString();
        return `fleet/organizations/?${queryString}`;
      },
      providesTags: ['Organization'],
    }),

    // Organization detail with all groups (for expanded row)
    fetchOrganizationDetail: builder.query({
      query: ({ orgId, plan, includeDetails }) => {
        const queryParams = new URLSearchParams();
        if (plan) queryParams.append('plan', plan);
        if (includeDetails) queryParams.append('include_details', 'true');
        const queryString = queryParams.toString();
        return `fleet/organizations/${orgId}/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (result, error, { orgId }) => [
        { type: 'Organization', id: orgId },
        'GroupContacts',
      ],
    }),

    // Batch update monitoring config for all groups under an org
    updateOrganizationMonitoring: builder.mutation({
      query: ({ orgId, ...body }) => ({
        url: `fleet/organizations/${orgId}/monitoring/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { orgId }) => [
        { type: 'Organization', id: orgId },
      ],
    }),

    // ========== ORGANIZATION DUPLICATES (Optimized) ==========
    fetchOrganizationDuplicates: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.threshold) queryParams.append('threshold', params.threshold);
        if (params.page) queryParams.append('page', params.page);
        if (params.pageSize) queryParams.append('page_size', params.pageSize);
        const queryString = queryParams.toString();
        return `fleet/organizations/duplicates/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Organization'],
    }),

    // ========== ORGANIZATION MERGE ==========
    previewMerge: builder.query({
      query: ({ sourceId, targetId }) =>
        `fleet/organizations/merge/preview/?source_id=${sourceId}&target_id=${targetId}`,
    }),

    mergeOrganizations: builder.mutation({
      query: ({ sourceId, targetId, reason }) => ({
        url: `fleet/organizations/merge/`,
        method: 'POST',
        body: { source_id: sourceId, target_id: targetId, reason },
      }),
      invalidatesTags: ['PendingGroups', 'Organization', 'DeviceGroup'],
    }),

    // ========== GROUP CONTACTS ==========
    fetchGroupContacts: builder.query({
      query: (groupId) => `fleet/groups/${groupId}/contacts/manage/`,
      providesTags: (result, error, groupId) => [{ type: 'GroupContacts', id: groupId }],
    }),

    updateGroupContact: builder.mutation({
      query: ({ groupId, contactData }) => ({
        url: `fleet/groups/${groupId}/contacts/manage/`,
        method: 'POST',
        body: contactData,
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'GroupContacts', id: groupId },
        'GroupContacts',
        'Organization',
      ],
    }),

    resetGroupContacts: builder.mutation({
      query: (groupId) => ({
        url: `fleet/groups/${groupId}/contacts/reset/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, groupId) => [
        { type: 'GroupContacts', id: groupId },
        'GroupContacts',
        'Organization',
      ],
    }),

    // ========== GROUP PLAN ==========
    updateGroupPlan: builder.mutation({
      query: ({ groupId, plan }) => ({
        url: `fleet/groups/${groupId}/plan/`,
        method: 'PATCH',
        body: { plan },
      }),
      invalidatesTags: ['Organization', 'DeviceGroup'],
    }),

    // ========== GROUP VEHICLES (On-demand) ==========
    fetchGroupVehicles: builder.query({
      query: (groupId) => `fleet/groups/${groupId}/vehicles/`,
      providesTags: (result, error, groupId) => [{ type: 'DeviceGroup', id: groupId }],
    }),

    // ========== ORGANIZATION CONTACTS ==========
    propagateOrgContacts: builder.mutation({
      query: ({ orgId, groupIds, forceOverwrite = false, forceCreate = true }) => {
        const body = {
          force_overwrite: Boolean(forceOverwrite),
          force_create: Boolean(forceCreate),
        };
        if (groupIds) {
          body.group_ids = groupIds;
        }
        return {
          url: `fleet/organizations/${orgId}/propagate-contacts/`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['GroupContacts'],
    }),
  }),
});

export const {
  // Pending Groups
  useFetchPendingGroupsQuery,
  // Group Verification
  useVerifyGroupMutation,
  useRejectGroupMutation,
  useReassignGroupMutation,
  useBulkVerifyGroupsMutation,
  useBulkRejectGroupsMutation,
  // Organization List & Duplicates (Optimized)
  useFetchOrganizationsQuery,
  useLazyFetchOrganizationsQuery,
  useFetchOrganizationsWithStatsQuery,
  useLazyFetchOrganizationsWithStatsQuery,
  useFetchOrganizationDetailQuery,
  useLazyFetchOrganizationDetailQuery,
  useUpdateOrganizationMonitoringMutation,
  useFetchOrganizationDuplicatesQuery,
  useLazyFetchOrganizationDuplicatesQuery,
  // Organization Merge
  usePreviewMergeQuery,
  useLazyPreviewMergeQuery,
  useMergeOrganizationsMutation,
  // Group Contacts
  useFetchGroupContactsQuery,
  useUpdateGroupContactMutation,
  useResetGroupContactsMutation,
  // Group Plan
  useUpdateGroupPlanMutation,
  // Group Vehicles
  useFetchGroupVehiclesQuery,
  // Organization Contacts
  usePropagateOrgContactsMutation,
} = groupManagementApiSlice;
